import { FormContentIsland, FormTextField, ListContentIsland } from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { AccountField } from '../../Field/AccountField';
import { TransferBeneficiaryListItem } from './TransferBeneficiaryListItem';
import {
  TransferFormValues,
  isCommentApplicable,
  isEvidenceApplicable,
  isEvidenceRequired,
  isPopCodeApplicable,
  isReferenceApplicable,
} from './types';
import { UseFormReturn } from 'react-hook-form';
import { AmountField } from '../../Field/AmountField';
import { ApiAccount, ApiExternalOutgoingTransferIntentResponse } from '../../../openapi';
import { OutgoingTransferBreakdown } from '../../TransferBreakdown/OutgoingTransferBreakdown';
import { PopCodeField } from '../../Field/PopCodeField';
import { FileDropField } from '../../Field/FileDropZone';
import {
  useExternalOutgoingProviderQuery,
  useUploadEvidenceMutation,
} from '../../../api/transfers';
import { useFormError } from '../../../hooks/useFormError';

export function TransferAmountStep({
  form,
  accounts,
  intent,
  onMaxClick,
}: {
  form: UseFormReturn<TransferFormValues>;
  accounts: ApiAccount[];
  intent?: ApiExternalOutgoingTransferIntentResponse | null;
  onMaxClick?: () => void;
}) {
  const { t } = useTranslation();
  const accountId = form.watch('accountId');
  const beneficiary = form.watch('beneficiary');
  const country = form.watch('beneficiary.value.bankCountry');
  const currency = beneficiary.value.currency;

  const providerQuery = useExternalOutgoingProviderQuery(
    country && currency ? { country, currency } : undefined,
  );

  useFormError(form, 'accountId', providerQuery.error);

  const uploadEvidenceMutation = useUploadEvidenceMutation();

  return (
    <>
      <FormContentIsland title={t('From')}>
        <AccountField
          name="accountId"
          label={t('Account')}
          accounts={accounts}
          loading={!accountId}
          required
          disableClearable
        />
        <AmountField
          name="clientAmount"
          label={t('Amount')}
          currency={currency}
          onMaxClick={onMaxClick}
          required
          autoFocus
        />
        {isPopCodeApplicable(beneficiary) ? (
          <PopCodeField
            name="popCode"
            label={t('Purpose of payment')}
            country={country}
            currency={currency}
            provider={providerQuery.data?.provider}
            required
          />
        ) : null}
        {isReferenceApplicable(beneficiary) ? (
          <FormTextField name="reference" label={t('Reference (optional)')} maxLength={100} />
        ) : null}
        {isCommentApplicable(beneficiary) ? (
          <FormTextField name="comment" label={t('Comment (optional)')} />
        ) : null}
        {isEvidenceApplicable(beneficiary) ? (
          <FileDropField
            name="evidence"
            label={t('Supporting documents')}
            accept={{
              'image/*': ['.png', '.jpg', '.jpeg'],
              'application/pdf': ['.pdf'],
            }}
            // if user has uploaded evidence but upload failed,
            // we should allow to proceed without re-uploading
            required={
              isEvidenceRequired(beneficiary, intent?.amount) && !uploadEvidenceMutation.isError
            }
            maxFiles={5}
            mutation={uploadEvidenceMutation}
          />
        ) : null}
      </FormContentIsland>
      {typeof intent === 'undefined' ? null : (
        <OutgoingTransferBreakdown variant="form" intent={intent} />
      )}
      <ListContentIsland title={t('To')}>
        <TransferBeneficiaryListItem beneficiary={beneficiary} />
      </ListContentIsland>
    </>
  );
}
